/**
 * WalletChip — balance + deposit CTA. Two sizes: `sm` (topbar) + `lg` (dashboard hero).
 * Both variants respect the app-wide display currency (T7).
 */
import { Link } from "react-router-dom";
import { Plus, Wallet } from "lucide-react";
import { useCurrency } from "../../context/CurrencyContext";
import { formatAmount as formatAmountForCode } from "../../lib/currency";

export default function WalletChip({ real = 0, demo = 0, mode = "real", onModeChange, size = "sm", currency: currencyOverride, testid = "wallet-chip" }) {
  const { format, currency } = useCurrency();
  const active = mode === "real" ? real : demo;
  if (size === "lg") {
    return (
      <div className="grid grid-cols-2 gap-3" data-testid={testid}>
        <BalanceCard label="Real balance" amount={real} currency={currencyOverride} tone="gold" cta="Deposit" to={`/wallet/deposit${currencyOverride ? `?currency=${currencyOverride}` : ""}`} testid="wallet-chip-real" />
        <BalanceCard label="Demo balance" amount={demo} currency={currencyOverride} tone="muted" cta="Top up demo" to="/game" testid="wallet-chip-demo" />
      </div>
    );
  }
  return (
    <div
      className="inline-flex items-center gap-2 rounded-lg border border-line bg-elevated px-1 py-1"
      data-testid={testid}
    >
      <button
        onClick={() => onModeChange && onModeChange(mode === "real" ? "demo" : "real")}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-highest transition"
        data-testid="wallet-chip-mode-toggle"
        title="Switch mode"
      >
        <Wallet className={`w-3.5 h-3.5 ${mode === "real" ? "text-gold" : "text-ink-2"}`} />
        <div className="text-left leading-tight">
          <div className="text-[9px] tracking-widest uppercase text-ink-3">{mode}</div>
          <div className="font-mono text-sm text-ink" data-currency={currency}>{format(active)}</div>
        </div>
      </button>
      <Link
        to="/wallet/deposit"
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-brand-gradient text-white text-xs font-medium hover:brightness-110 shadow-glow-pink"
        data-testid="wallet-chip-deposit-btn"
      >
        <Plus className="w-3 h-3" />
        Deposit
      </Link>
    </div>
  );
}

function BalanceCard({ label, amount, currency: currencyOverride, tone, cta, to, testid }) {
  const { format, currency: activeCurrency } = useCurrency();
  const display = currencyOverride
    // Format using the override (native-currency amount) instead of the app-wide active currency.
    ? formatAmountForCode(amount, currencyOverride)
    : format(amount);
  const gradient = tone === "gold" ? "from-brand-pink/10 to-purple/10 border-pink/30" : "from-highest to-elevated border-line";
  const amountClass = tone === "gold" ? "gold-text" : "text-ink";
  return (
    <div
      className={`relative overflow-hidden rounded-xl border p-6 bg-gradient-to-br ${gradient}`}
      data-testid={testid}
    >
      <div className="text-[10px] tracking-[0.3em] uppercase text-ink-2 mb-2">{label}</div>
      <div
        className={`font-display italic text-4xl ${amountClass} mb-4`}
        data-testid={`${testid}-amount`}
        data-currency={currencyOverride || activeCurrency}
      >
        {display}
      </div>
      <Link
        to={to}
        className={
          tone === "gold"
            ? "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-gradient text-white text-xs font-medium hover:brightness-110 shadow-glow-pink transition"
            : "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-line text-xs text-ink-2 hover:text-ink hover:border-ink-2 transition"
        }
        data-testid={`${testid}-cta`}
      >
        <Plus className="w-3 h-3" />
        {cta}
      </Link>
    </div>
  );
}
